
import { SUPABASE_URL } from '@/utils/supabaseConfig';
import { getApiHeaders, handleApiError } from './apiUtils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { deleteRentalWithHistory } from './rentalDeleteService';
import logger from '@/utils/logger';

// Экспортируем updateBikeStatus
export const updateBikeStatus = async (
  bikeId: string,
  status: string
): Promise<{ success: boolean; error: any }> => {
  try {
    const { error } = await supabase
      .from('bikes')
      .update({ status })
      .eq('id', bikeId);
    
    if (error) {
      logger.error('Ошибка при обновлении статуса велосипеда:', error);
      return { success: false, error };
    }
    
    return { success: true, error: null };
  } catch (error) {
    logger.error('Непредвиденная ошибка при обновлении статуса велосипеда:', error);
    return { success: false, error };
  }
};

// Функция для обновления статуса аренды через Supabase клиент
export const updateRentalStatus = async (
  rentalId: string,
  updateData: any
): Promise<{ success: boolean; error: any }> => {
  try {
    // Преобразуем строку в объект, если передана только строка статуса
    const dataToUpdate = typeof updateData === 'string' ? { status: updateData } : updateData;
    
    // Логируем отправляемые данные для отладки
    logger.debug('Обновление статуса аренды, ID:', rentalId, 'данные:', dataToUpdate);
    
    const { error } = await supabase
      .from('rentals')
      .update(dataToUpdate)
      .eq('id', rentalId);
    
    if (error) {
      logger.error('Ошибка при обновлении статуса аренды:', error);
      return { success: false, error };
    }
    
    return { success: true, error: null };
  } catch (error) {
    logger.error('Непредвиденная ошибка при обновлении статуса аренды:', error);
    return { success: false, error };
  }
};

// Функция для создания новой аренды через Supabase клиент
export const createRental = async (
  rentalData: any
): Promise<{ data: any | null; error: any }> => {
  try {
    const { data, error } = await supabase
      .from('rentals')
      .insert(rentalData)
      .select();
    
    if (error) {
      logger.error('Ошибка при создании аренды:', error);
      return { data: null, error };
    }
    
    return { data, error: null };
  } catch (error) {
    logger.error('Непредвиденная ошибка при создании аренды:', error);
    return { data: null, error };
  }
};

// Функция для обновления данных аренды через Supabase клиент
export const updateRental = async (
  rentalId: string,
  rentalData: any
): Promise<{ data: any | null; error: any }> => {
  try {
    // Логируем детали запроса для отладки
    logger.debug('Отправка запроса на обновление аренды:', {
      id: rentalId,
      data: rentalData
    });
    
    const { data, error } = await supabase
      .from('rentals')
      .update(rentalData)
      .eq('id', rentalId)
      .select();
    
    if (error) {
      logger.error('Ошибка при обновлении аренды:', error);
      toast.error(`Ошибка при обновлении: ${error.message}`);
      return { data: null, error };
    }
    
    logger.info('Аренда успешно обновлена:', data);
    
    // Если данные не возвращаются, создаем объект с ID и обновленными данными
    if (!data || data.length === 0) {
      return { data: { id: rentalId, ...rentalData }, error: null };
    }
    
    return { data, error: null };
  } catch (error: any) {
    logger.error('Непредвиденная ошибка при обновлении аренды:', error);
    toast.error(`Непредвиденная ошибка: ${error.message || 'Неизвестная ошибка'}`);
    return { data: null, error };
  }
};

// Обновленная функция для удаления аренды с каскадным удалением связанных данных
export const deleteRental = async (
  rentalId: string
): Promise<{ success: boolean; error: any }> => {
  try {
    // Логируем операцию удаления для отладки
    logger.debug('Выполняем каскадное удаление аренды с ID:', rentalId);
    
    // Используем новую функцию каскадного удаления
    const result = await deleteRentalWithHistory(rentalId);
    
    if (!result.success) {
      logger.error('Ошибка при каскадном удалении аренды:', result.error);
      toast.error(`Ошибка при удалении: ${result.error.message || 'Неизвестная ошибка'}`);
      return { success: false, error: result.error };
    }
    
    logger.info('Аренда и все связанные данные успешно удалены:', rentalId);
    return { success: true, error: null };
  } catch (error: any) {
    logger.error('Непредвиденная ошибка при удалении аренды:', error);
    toast.error(`Непредвиденная ошибка: ${error.message || 'Неизвестная ошибка'}`);
    return { success: false, error };
  }
};
